import { useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { menuService } from "@/services/breeze/MenuService";
import { useAuth } from "@/providers/AuthProvider";
import {
  DefaultItems,
  DefaultMenuItem,
  TenantAdminItems,
} from "@/config/navigation";
import { useFeatureFlag } from "./useFeatureFlag";

export function useNavigation() {
  const pathname = useLocation().pathname;
  const { currentTenant, safeNavigate, isTenantAdmin } = useAuth();
  const [menuItems, setMenuItems] = useState<DefaultMenuItem[]>([
    ...DefaultItems,
  ]);
  const [activeSection, setActiveSection] = useState("dashboard");
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);
  const [activeSubItem, setActiveSubItem] = useState<string | null>(null);

  const aiAgentsEnabled = useFeatureFlag("AnythinkAIAgent");

  const { data: myMenu } = useQuery({
    queryKey: ["my-menu"],
    queryFn: () => menuService.getMyMenu(),
  });

  useEffect(() => {
    const menuItems = [...DefaultItems];

    if (isTenantAdmin) {
      // Filter out AI Agents if feature flag is disabled
      const adminItems = aiAgentsEnabled
        ? TenantAdminItems
        : TenantAdminItems.filter((item) => item.display_name !== "AI Agents");
      menuItems.push(...adminItems);
    }

    if (myMenu) {
      menuItems.push(...myMenu.items.filter((item) => item.parent_id === null));
    }

    setMenuItems(menuItems.sort((a, b) => a.sort_order - b.sort_order));
  }, [myMenu, currentTenant, aiAgentsEnabled, isTenantAdmin]);

  // Update active section based on pathname
  useEffect(() => {
    // Find the matching section based on the pathname
    const matchingSection = menuItems.find((section) => {
      // First check if any sub-item matches exactly
      if (section.items?.some((subItem) => pathname === subItem.href)) {
        return true;
      }

      // If no sub-items match, check if the main item matches
      // Skip items with # as href since they're not navigable
      if (section.href === "#") {
        return false;
      }

      // Check exact match
      if (pathname === section.href) {
        return true;
      }

      // Check if pathname starts with the section href (for nested routes)
      const normalizedSectionHref = section.href.startsWith("/")
        ? section.href
        : `/${section.href}`;
      if (
        pathname.startsWith(normalizedSectionHref) &&
        normalizedSectionHref !== "/"
      ) {
        return true;
      }

      // Check with tenant prefix
      if (currentTenant) {
        const tenantPrefixedHref = `/org/${currentTenant.id}${normalizedSectionHref}`;
        if (pathname.startsWith(tenantPrefixedHref)) {
          return true;
        }
      }

      return false;
    });

    if (matchingSection) {
      setActiveSection(matchingSection.id.toString());

      // Auto-expand if this section has sub-items or if a sub-item is active
      if (matchingSection.items && matchingSection.items.length > 0) {
        setExpandedMenu(matchingSection.id.toString());
      }

      // Find matching subItem
      const matchingSubItem = matchingSection.items?.find(
        (subItem) => pathname === subItem.href,
      );
      setActiveSubItem(matchingSubItem?.href || null);
    }
  }, [pathname, menuItems, currentTenant]);

  // Handle navigation item click
  const handleItemClick = (id: string) => {
    const item = menuItems.find((item) => item.id.toString() === id);
    if (!item) return;

    // If the item has a # href, it's not navigable - just toggle expansion
    if (item.href === "#") {
      if (expandedMenu === id) {
        setExpandedMenu(null);
      } else {
        setExpandedMenu(id);
      }
      return;
    }

    // For navigable items
    if (expandedMenu === id) {
      // If already expanded and clicked again, collapse it
      setExpandedMenu(null);
      // Only navigate if this isn't already the active section
      if (activeSection !== id) {
        safeNavigate(item.href);
      }
    } else {
      // Expand the menu and navigate
      setExpandedMenu(id);
      safeNavigate(item.href);
    }
  };

  // Handle sub-item click
  const handleSubItemClick = (parentId: string, subItemLabel: string) => {
    const parent = menuItems.find((item) => item.id.toString() === parentId);
    if (!parent) return;

    const subItem = parent.items?.find(
      (item) => item.display_name === subItemLabel,
    );
    if (!subItem) return;

    setActiveSubItem(subItem.href);
    safeNavigate(subItem.href);
  };

  return {
    menuItems,
    activeSection,
    expandedMenu,
    activeSubItem,
    handleItemClick,
    handleSubItemClick,
  };
}
